/**
 * Cost Tracking query and mutation helpers using Gen 2 typed client.
 */
import { authClient } from "../utils/amplifyClient";
import { getGraphQlErrorMessage } from "../utils/graphqlErrorUtils";

function throwIfGraphQlErrors(result: { errors?: unknown }, fallback: string) {
    const errors = result.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        throw new Error(getGraphQlErrorMessage(result) || fallback);
    }
}

/** Optional fields that may be missing until the backend schema catches up. */
const EXPENSE_RECORD_OPTIONAL_FIELDS = ['pmPartsSnapshot', 'laborEntries'] as const;
const EXPENSE_CATEGORY_OPTIONAL_FIELDS = ['equipmentCategoryId'] as const;

/**
 * Fields present on the input that an unknown-field GraphQL error names.
 * Used to strip-and-retry saves before a schema deploy.
 */
export function optionalFieldsNamedInError(
    err: unknown,
    input: Record<string, unknown>,
    optionalFields: readonly string[],
): string[] {
    const message = getGraphQlErrorMessage(err);
    if (!message) return [];
    return optionalFields.filter(
        (field) => input[field] !== undefined && new RegExp(field, 'i').test(message),
    );
}

function omitFields(input: Record<string, unknown>, fields: string[]): Record<string, unknown> {
    if (fields.length === 0) return input;
    const next = { ...input };
    for (const field of fields) {
        delete next[field];
    }
    return next;
}

async function commitWithOptionalFieldRetry<T>(
    input: Record<string, unknown>,
    optionalFields: readonly string[],
    run: (payload: Record<string, unknown>) => Promise<{ data: T; errors?: unknown }>,
    fallback: string,
): Promise<T> {
    let payload: Record<string, unknown> = input;
    for (;;) {
        try {
            const result = await run(payload);
            throwIfGraphQlErrors(result, fallback);
            return result.data;
        } catch (err) {
            const drop = optionalFieldsNamedInError(err, payload, optionalFields);
            if (drop.length === 0) throw err;
            payload = omitFields(payload, drop);
        }
    }
}

// Derive the exact create/update input shapes from the typed client (repo pattern).
type CreateExpenseRecordInput = Parameters<typeof authClient.models.ExpenseRecord.create>[0];
type UpdateExpenseRecordInput = Parameters<typeof authClient.models.ExpenseRecord.update>[0];
type CreateExpenseCategoryInput = Parameters<typeof authClient.models.ExpenseCategory.create>[0];
type UpdateExpenseCategoryInput = Parameters<typeof authClient.models.ExpenseCategory.update>[0];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function saveExpenseRecord(input: Record<string, any>, mode: "create" | "update") {
    return commitWithOptionalFieldRetry(
        input,
        EXPENSE_RECORD_OPTIONAL_FIELDS,
        (payload) =>
            mode === "create"
                ? authClient.models.ExpenseRecord.create(payload as CreateExpenseRecordInput)
                : authClient.models.ExpenseRecord.update(payload as UpdateExpenseRecordInput),
        "Failed to save expense record",
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function saveExpenseCategory(input: Record<string, any>, mode: "create" | "update") {
    return commitWithOptionalFieldRetry(
        input,
        EXPENSE_CATEGORY_OPTIONAL_FIELDS,
        (payload) =>
            mode === "create"
                ? authClient.models.ExpenseCategory.create(payload as CreateExpenseCategoryInput)
                : authClient.models.ExpenseCategory.update(payload as UpdateExpenseCategoryInput),
        "Failed to save expense category",
    );
}

// ---------------------------------------------------------------------------
// Query helpers — ExpenseRecord
// ---------------------------------------------------------------------------

const EXPENSE_LIST_PAGE_LIMIT = 1000;
const EXPENSE_LIST_MAX_PAGES = 20;

async function listEveryExpensePage<T>(
    load: (nextToken?: string | null) => Promise<{ data: T[]; nextToken?: string | null }>,
): Promise<T[]> {
    const items: T[] = [];
    const seenTokens = new Set<string>();
    let nextToken: string | null | undefined;

    for (let page = 0; page < EXPENSE_LIST_MAX_PAGES; page += 1) {
        const result = await load(nextToken);
        items.push(...(result.data ?? []));
        const token = result.nextToken ?? null;
        if (!token || seenTokens.has(token)) break;
        seenTokens.add(token);
        nextToken = token;
    }

    return items;
}

export async function fetchExpenseRecordsByCompany(companyId: string) {
    return listEveryExpensePage((nextToken) =>
        authClient.models.ExpenseRecord.listExpenseRecordByCompanyIdAndDateOfService(
            { companyId },
            { limit: EXPENSE_LIST_PAGE_LIMIT, nextToken },
        ),
    );
}

export async function fetchExpenseRecordsByUnit(unitId: string) {
    return listEveryExpensePage((nextToken) =>
        authClient.models.ExpenseRecord.listExpenseRecordByUnitIdAndDateOfService(
            { unitId },
            { limit: EXPENSE_LIST_PAGE_LIMIT, nextToken },
        ),
    );
}

// ---------------------------------------------------------------------------
// Mutation helpers — ExpenseRecord
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createExpenseRecord(input: any) {
    return saveExpenseRecord(input, "create");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateExpenseRecord(input: any) {
    return saveExpenseRecord(input, "update");
}

export async function deleteExpenseRecord(id: string) {
    const { data } = await authClient.models.ExpenseRecord.delete({ id });
    return data;
}

// ---------------------------------------------------------------------------
// Query helpers — ExpenseCategory
// ---------------------------------------------------------------------------

export async function fetchExpenseCategoriesByCompany(companyId: string) {
    const { data } = await authClient.models.ExpenseCategory.listExpenseCategoryByCompanyId({ companyId });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createExpenseCategory(input: any) {
    return saveExpenseCategory(input, "create");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateExpenseCategory(input: any) {
    return saveExpenseCategory(input, "update");
}

// ---------------------------------------------------------------------------
// Query helpers — UnitExpenseBudget
// ---------------------------------------------------------------------------

export async function fetchUnitExpenseBudgetsByCompany(companyId: string) {
    const { data } = await authClient.models.UnitExpenseBudget.listUnitExpenseBudgetByCompanyId({ companyId });
    return data;
}

export async function fetchUnitExpenseBudgetsByUnit(unitId: string) {
    const { data } = await authClient.models.UnitExpenseBudget.listUnitExpenseBudgetByUnitId({ unitId });
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function createUnitExpenseBudget(input: any) {
    const { data } = await authClient.models.UnitExpenseBudget.create(input);
    return data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateUnitExpenseBudget(input: any) {
    const { data } = await authClient.models.UnitExpenseBudget.update(input);
    return data;
}
